import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useLocation, useNavigate } from 'react-router-dom';
import { Avatar } from '../primitives';
import { useAuthContext } from '../../contexts/AuthContext';
import { useBoot } from '../../contexts/BootContext';
import { useAthleteContext } from '../../hooks/useAthleteContext';
import { useGuardianMode } from '../../hooks/useGuardianMode';
import { useServerFlag } from '../../hooks/useServerFlag';
import { accountFullName } from '../../lib/accountView';
import { openSettingsFrom } from '../../lib/nav/settingsOrigin';
import { isNativeApp } from '../../lib/platform';
import { useSignOutWithConfirm } from '../account/v2/SignOutConfirm';
import { ViewSwitch } from '../account/v2/ViewSwitch';
import { BellTrigger } from '../SideNav/BellTrigger';
import { BugReportTrigger } from '../SideNav/BugReportTrigger';
import { TayroMark } from '../brand/TayroMark';
import { AskCoachPill } from '../Fab/AskCoachPill';
import styles from './TopBar.module.css';

type TopBarProps = {
  userInitial: string;
  /**
   * W2-CHROME (CH-9) — the full name shown above the email in the account
   * menu and sheet (`settings_2_0_v1` on). Optional: no mounting point passes
   * it, so the menu resolves the name itself (athlete context `Name`, else the
   * sign-in provider's name, never the email). A caller that already knows the
   * name may pass it and wins.
   */
  userName?: string;
  /**
   * @deprecated PR 1c-b — the bell is now wired to the real
   * notification feed via `<BellTrigger variant="icon-button" />`.
   * The static `hasNotification` boolean is ignored; the badge now
   * reflects live unread count from `useNotifications`.
   */
  hasNotification?: boolean;
};

/**
 * B32 (harvest 2026-09-26b HIGH finding) — the CSS custom property this bar
 * publishes its own measured BOTTOM edge to, so chrome that has to dock
 * directly below it (SettingsShell's mobile bucket-tab bar, B259) can read a
 * real measurement instead of a guessed constant. Published on
 * `document.documentElement` — the established host for a cross-component
 * measured CSS var in this codebase (formKeyboard.ts's `--kb-inset`,
 * nativeKeyboard.ts's keyboard-height var) — because TopBar and that chrome
 * are siblings under the route's frame, not ancestor/descendant, so a var
 * scoped to the bar itself would never cascade to it.
 */
export const TOPBAR_BOTTOM_VAR = '--topbar-bottom';

/**
 * Mobile top bar — sticky header with logo, notification bell, avatar.
 * 390px layout. Desktop uses SideNav instead.
 *
 * **PR 1c-b update**: the previously-decorative bell SVG button is
 * replaced with `<BellTrigger variant="icon-button" />`, which wires
 * to the real notification feed (60s poll · 5-key allowlist · click
 * opens a bottom-sheet dropdown · escalation-modal handoff). Visual
 * shape (32px circular paper-bg + ink-200 border) is preserved so
 * the existing layout reads identically — just functional now.
 *
 * **B1 update (2026-05-16)**: the Avatar is now a tap target. Tapping
 * opens a tiny user-menu popover containing "Sign out". Two-step
 * gesture (tap avatar → tap "Sign out") prevents accidental signouts
 * from a stray thumb. When no auth user, the avatar renders inert
 * (the menu would have nothing to show). Menu closes on outside-click
 * + Escape.
 *
 * **2026-06-12 (user report)**: the menu now also shows WHO is signed
 * in (the account email — "I wanted to verify I was on the correct
 * account" had no answer on mobile) and links to Settings (previously
 * unreachable from the TopBar; the BottomTab ⚙️ only renders when
 * signed in and the plan pages have no other chrome).
 *
 * **Mobile v1 D10 (Capacitor shell ONLY — isNativeApp())**: the
 * signed-out avatar rendered inert-but-tappable-looking and misled the
 * founder; in the shell it becomes a "Sign in" text button → /welcome.
 * The signed-in account menu opens as a BOTTOM SHEET (the high-right
 * popover sat out of thumb reach; reuses the --sheet-… + --notif-scrim
 * tokens), and in-shell sign-out lands on /welcome, never the web
 * landing. Web behavior is byte-identical to before.
 */
export function TopBar({ userInitial, userName }: TopBarProps) {
  const { user: authUser, signOut } = useAuthContext();
  // W2-CHROME — the Part 5 account menu (full name, one View switch, a Settings
  // origin, Sign out that asks first). Read with the id as a literal; with the
  // flag off every branch below is the original code path. The sign-out
  // question's hook sits up here, not in the menu body, because the dialog has
  // to outlive the menu that opened it (the menu closes on "Sign out").
  const settingsOn = useServerFlag('settings_2_0_v1');
  const signOutFlow = useSignOutWithConfirm();
  // D10 — one switch, read per render; false everywhere on the web.
  const native = isNativeApp();
  // BOOT §4 — during the COLD signed-in handoff the BootVeil's traveling mark
  // docks into this slot; suppress our own mark until it lands (measured via
  // the [data-boot-dock] wrapper below). Always false on web → byte-identical.
  const { docking } = useBoot();
  // CG-4 (design N2, founder-ratified) — dual accounts reach the
  // guardian world via the account menu's "Switch to guardian view";
  // renders only when the user actively guards someone (cached check).
  const { isGuardian } = useGuardianMode();
  const navigate = useNavigate();
  const [menuOpen, setMenuOpen] = useState(false);
  const menuRef = useRef<HTMLDivElement | null>(null);
  const triggerRef = useRef<HTMLButtonElement | null>(null);

  // B-33 round 3 — the bar is now `position: fixed` (see TopBar.module.css:
  // WKWebView re-anchors a `position: sticky` element on the MAIN thread during
  // momentum scroll, which shook; a fixed element never re-anchors — the same
  // strategy that keeps the BottomTab rock-steady). A fixed bar leaves the flow,
  // so the document (the scroll container) would slide route content up UNDER
  // it. We reserve its exact height with an in-flow spacer sibling rendered
  // right where the bar used to sit — so every route that mounts <TopBar/> gets
  // the correct top offset with ZERO per-route magic numbers, and the flow
  // footprint stays byte-identical to the old sticky bar (anchors / scroll-to
  // math unchanged). The height is MEASURED (not a hardcoded token) so it tracks
  // the real chrome across signed-in vs signed-out and any safe-area reflow.
  const barRef = useRef<HTMLElement | null>(null);
  const [barHeight, setBarHeight] = useState<number | null>(null);
  useLayoutEffect(() => {
    const el = barRef.current;
    if (!el) return;
    // useLayoutEffect measures BEFORE paint, so the spacer is sized on the very
    // first frame — no content jump. `top`/safe-area offset is NOT part of this
    // height (body already carries padding-top: safe-area-inset-top in the
    // shell, global.css); the spacer only reserves the bar's own box.
    //
    // B32 — `rect.bottom` is the bar's viewport-relative bottom edge, which
    // DOES already carry the native `top: env(safe-area-inset-top)` offset
    // (the `html.native-app header` rule, global.css) — unlike `barHeight`
    // above, it is exactly what chrome docking below this bar needs to
    // position itself against, on web and native alike.
    const measure = (): void => {
      const rect = el.getBoundingClientRect();
      setBarHeight(rect.height);
      document.documentElement.style.setProperty(TOPBAR_BOTTOM_VAR, `${rect.bottom}px`);
    };
    measure();
    let ro: ResizeObserver | undefined;
    if (typeof ResizeObserver !== 'undefined') {
      ro = new ResizeObserver(measure);
      ro.observe(el);
    }
    return () => {
      ro?.disconnect();
      document.documentElement.style.removeProperty(TOPBAR_BOTTOM_VAR);
    };
  }, []);

  // Outside-click + Escape close the menu. Pointerdown (not click) so
  // we close BEFORE a downstream component sees the tap and reacts.
  useEffect(() => {
    if (!menuOpen) return;
    const onPointerDown = (e: PointerEvent) => {
      const tgt = e.target as Node | null;
      if (!tgt) return;
      if (menuRef.current?.contains(tgt)) return;
      if (triggerRef.current?.contains(tgt)) return;
      setMenuOpen(false);
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setMenuOpen(false);
    };
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [menuOpen]);

  const handleSignOut = async () => {
    setMenuOpen(false);
    try {
      await signOut();
    } catch {
      /* clears local state regardless */
    }
    // D10 — in the shell every signed-out path lands on the native
    // welcome; the web keeps its marketing landing.
    navigate(native ? '/welcome' : '/landing');
  };

  // Founder round 4 — the sheet handle LOOKED draggable but was
  // decorative. Standard iOS grammar: drag down past the threshold
  // dismisses; short pulls spring back (tap still closes via scrim).
  const sheetDragStart = useRef<number | null>(null);
  const onSheetHandleDown = (e: React.PointerEvent) => {
    sheetDragStart.current = e.clientY;
    e.currentTarget.setPointerCapture(e.pointerId);
    menuRef.current?.classList.add(styles.sheetDragging);
  };
  const onSheetHandleMove = (e: React.PointerEvent) => {
    const start = sheetDragStart.current;
    const el = menuRef.current;
    if (start === null || !el) return;
    const dy = e.clientY - start;
    // Down follows the finger (dismiss gesture); UP rubber-bands — the
    // sheet already shows everything, and dead-feeling gestures read
    // as broken (founder round 5).
    el.style.transform = `translateY(${dy > 0 ? dy : Math.max(dy * 0.12, -14)}px)`;
  };
  const endSheetDrag = (e: React.PointerEvent, apply: boolean) => {
    const start = sheetDragStart.current;
    sheetDragStart.current = null;
    const el = menuRef.current;
    if (el) {
      el.classList.remove(styles.sheetDragging);
      el.style.transform = '';
    }
    if (apply && start !== null && e.clientY - start > 60) setMenuOpen(false);
  };

  // Shared menu body — identical rows for the web popover (.menu) and the
  // native bottom sheet (.sheet); the native sheet adds a drag handle above
  // these. Defined once so the two render paths (inline vs portaled) can't
  // drift. `authUser` is non-null wherever this renders (guarded below).
  const accountMenuItems = settingsOn ? (
    <AccountMenuBodyV2
      email={authUser?.email ?? null}
      userName={userName}
      onClose={() => setMenuOpen(false)}
      onRequestSignOut={signOutFlow.requestSignOut}
    />
  ) : (
    <>
      {authUser?.email ? (
        <div className={styles.menuIdentity} aria-label="Signed in as">
          {authUser.email}
        </div>
      ) : null}
      {isGuardian && (
        <button
          type="button"
          role="menuitem"
          className={styles.menuItem}
          onClick={() => {
            setMenuOpen(false);
            navigate('/guardian');
          }}
        >
          Switch to guardian view
        </button>
      )}
      <button
        type="button"
        role="menuitem"
        className={styles.menuItem}
        onClick={() => {
          setMenuOpen(false);
          navigate('/settings');
        }}
      >
        Settings
      </button>
      <button
        type="button"
        role="menuitem"
        className={styles.menuItem}
        onClick={() => void handleSignOut()}
      >
        Sign out
      </button>
    </>
  );

  return (
    <>
    <header
      ref={barRef}
      // B374 (flag on): while the account menu is open the bar rises above the
      // Settings section nav and the tab bar, so the popover is never painted
      // under them. Flag off, or closed: the class list is the original.
      className={settingsOn && menuOpen ? `${styles.bar} ${styles.barMenuOpen}` : styles.bar}
      data-testid="topbar-bar"
    >
      <div className={styles.logo}>
        {native ? (
          // The measurable + hideable dock slot for the boot handoff. Same
          // 32px flex item as the bare mark, so layout is unchanged.
          <span
            data-boot-dock
            className={styles.markSlot}
            style={docking ? { visibility: 'hidden' } : undefined}
          >
            <TayroMark size={32} />
          </span>
        ) : (
          <TayroMark size={32} />
        )}
        <span className={styles.logoText}>Tayro</span>
      </div>
      {/* 2.0 chat (P9.1, Part 8 frame 1ao): the "Ask a coach" pill, between the logo and the right-hand controls.
          It draws nothing unless `chat_2_0_v1` is on and the account and route are the ones the pill is for
          (athlete and dual accounts on Today, Train, Races, Team and web Settings), so flag off the bar is
          exactly the two groups it always was. `TopBar.module.css` centres it when it is there. */}
      <AskCoachPill />
      <div className={styles.right}>
        <BugReportTrigger variant="icon-button" />
        <BellTrigger variant="icon-button" />
        {authUser ? (
          <div className={styles.avatarSlot}>
            <button
              ref={triggerRef}
              type="button"
              className={styles.avatarBtn}
              aria-label="Account menu"
              aria-expanded={menuOpen}
              aria-haspopup="menu"
              onClick={() => setMenuOpen((o) => !o)}
            >
              <Avatar initial={userInitial} size={32} />
            </button>
            {/* D10 — in the shell the scrim + bottom sheet replace the
                popover (thumb reach). Same items either way; the scrim
                sits outside menuRef so the existing pointerdown-outside
                handler closes the sheet on a scrim tap.
                B-33 — the native scrim + sheet (position: fixed) are
                PORTALED to document.body so they are NOT descendants of the
                sticky <header>. That lets the bar take a real compositor
                promotion (transform: translateZ(0)) without the transform
                becoming their containing block and re-parenting them into the
                57px bar. Geometry is identical (both are viewport-fixed) and
                z-index now stacks correctly above the BottomTab (the header's
                position:sticky/z-index:5 stacking context previously trapped
                the sheet below it). The web popover (.menu, position:absolute,
                anchored to .avatarSlot) is unaffected by the bar transform, so
                it stays inline. */}
            {menuOpen &&
              (native
                ? createPortal(
                    <>
                      <div className={styles.sheetScrim} aria-hidden="true" />
                      <div
                        ref={menuRef}
                        className={styles.sheet}
                        role="menu"
                        aria-label="Account"
                      >
                        <button
                          type="button"
                          className={styles.sheetHandleBtn}
                          aria-label="Close account menu"
                          onPointerDown={onSheetHandleDown}
                          onPointerMove={onSheetHandleMove}
                          onPointerUp={(e) => endSheetDrag(e, true)}
                          onPointerCancel={(e) => endSheetDrag(e, false)}
                        >
                          <span className={styles.sheetHandle} aria-hidden="true" />
                        </button>
                        {accountMenuItems}
                      </div>
                    </>,
                    document.body,
                  )
                : (
                    <div
                      ref={menuRef}
                      className={styles.menu}
                      role="menu"
                      aria-label="Account"
                    >
                      {accountMenuItems}
                    </div>
                  ))}
          </div>
        ) : native ? (
          /* D10 — the inert avatar looked tappable and did nothing. In
             the shell the welcome owns sign-in, so signed-out gets a
             clear way there instead. */
          <button
            type="button"
            className={styles.signInBtn}
            onClick={() => navigate('/welcome')}
          >
            Sign in
          </button>
        ) : (
          <Avatar initial={userInitial} size={32} />
        )}
      </div>
    </header>
      {/* B-33 round 3 — in-flow spacer reserving the fixed bar's height so route
          content starts below it (the document is the scroll container). Sized
          from the measured bar; renders 0-height until the first measure, which
          useLayoutEffect completes before paint. */}
      <div
        className={styles.barSpacer}
        aria-hidden="true"
        style={barHeight != null ? { height: barHeight } : undefined}
      />
      {/* W2-CHROME CH-6 — the sign-out question. Portaled to document.body by
          the hook (this bar is a transformed ancestor), so where it renders
          is irrelevant; null while closed and always null with the flag off. */}
      {settingsOn ? signOutFlow.dialog : null}
    </>
  );
}

/**
 * W2-CHROME (CH-2, CH-3, CH-7, CH-9) — the Part 5 account menu body, shared by
 * the web popover and the native sheet. Mounted only with `settings_2_0_v1` on,
 * so its hooks (the athlete context read, the router location, the View
 * switch's role reads) never run with the flag off.
 *
 *   identity    full name above the email when a name is known; the email
 *               alone otherwise, never a name derived from it
 *   View        one group, only when the account has two or more options
 *   Settings    opens through `openSettingsFrom`, so the back link can name
 *               the tab the user came from
 *   a rule, then Sign out last and apart, which asks first
 */
function AccountMenuBodyV2({
  email,
  userName,
  onClose,
  onRequestSignOut,
}: {
  email: string | null;
  userName?: string;
  onClose: () => void;
  onRequestSignOut: () => void;
}) {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { user } = useAuthContext();
  const { raw } = useAthleteContext();
  const name = (userName ?? '').trim() || accountFullName(user, raw);
  return (
    <>
      {name || email ? (
        <div className={`${styles.menuIdentity} ${styles.menuIdentityV2}`} aria-label="Signed in as">
          {name ? <span className={styles.menuName}>{name}</span> : null}
          {email ? <span className={styles.menuEmail}>{email}</span> : null}
        </div>
      ) : null}
      <ViewSwitch variant="menu" onChosen={onClose} />
      <button
        type="button"
        role="menuitem"
        className={`${styles.menuItem} ${styles.menuItemV2}`}
        onClick={() => {
          onClose();
          openSettingsFrom(navigate, pathname);
        }}
      >
        Settings
      </button>
      <div role="separator" className={styles.menuRule} />
      <button
        type="button"
        role="menuitem"
        className={`${styles.menuItem} ${styles.menuItemV2}`}
        onClick={() => {
          onClose();
          onRequestSignOut();
        }}
      >
        Sign out
      </button>
    </>
  );
}
